<template>
    <div class="toolbox-content">
        <h3>
            <button class="btn-headline btn-icon-only icon-edit" @click="value.step=2"></button>
            In Layer importieren
        </h3>
        <div class="formItem">
            <v-select
                    :value="value.selectedLayer"
                    class="sr-select"
                    :items="[value.selectedLayer]"
                    disabled
                    hide-details
                    item-value="id"
                    item-text="title"
            >
                <template #selection="{item}">
                    <i></i>
                    <span>
                                    <simple-svg
                                            :filepath="item.iconUrl"
                                            :fill="item.color"
                                            :stroke="item.stroke"
                                    ></simple-svg>
                                    <div class="layer-title">{{ item.title }}</div>
                    </span>
                </template>
            </v-select>
        </div>
        <div class="formItem">
            <label>Objekt gehört zu</label>
            <v-select
                    v-model="objectRights"
                    class="sr-select"
                    :items="getPossibleObjectRights"
                    hide-details
                    :rules="[ v => !!v || 'Objekt-Rechte müssen gesetzt sein' ]"
                    item-value="id"
                    item-text="title"
            ></v-select>
        </div>
        <div class="formItem">
            <div class="grid">
                <div class="col-1-2">
                    <div class="formItem">
                        <label>Eindeutige Id</label>
                        <v-tooltip bottom max-width="300">
                            <template #activator="{ on }">
                                <div v-on="on" class="styled-input">
                                    <strong>FlächenId</strong><span></span>
                                </div>
                            </template>
                            <span>Eine pro Layer eindeutige ID des Objektes</span>
                        </v-tooltip>
                    </div>
                </div>
                <div class="col-1-2">
                    <div class="formItem">
                        <label>Feld zuweisen</label>
                        <v-select
                                class="sr-select"
                                v-model="flaechenIdField"
                                :items="value.fileColumns"
                                hide-details
                                :rules="[ v => !!v || 'Eindeutige Id müssen gesetzt sein' ]"
                        ></v-select>
                    </div>
                </div>
            </div>
        </div>
        <div class="form-group">
            <h3>Feldzuweisungen</h3>
            <div class="form-label"><span>Im Import gefundene Felder</span></div>
            <div class="grid" v-for="fileColumn in value.fileColumns" :key="fileColumn">
                <div class="col-1-2">
                    <div class="formItem">
                        <label>Feld</label>
                        <v-tooltip bottom max-width="300">
                            <template #activator="{ on }">
                                <div v-on="on" class="styled-input">
                                    <strong>{{fileColumn}}</strong><span>{{ getExampleValues(fileColumn) }}</span>
                                </div>
                            </template>
                            <span>{{ getExampleValues(fileColumn) }}</span>
                        </v-tooltip>
                    </div>
                </div>
                <div class="col-1-2">
                    <div class="formItem">
                        <label>Feld zuweisen</label>
                        <v-autocomplete
                                class="sr-select"
                                @input="fileSelectChanged(fileColumn, $event)"
                                :items="availableLayerColumnsForFiles(fileColumn)"
                                :value="finalFileConfiguration[fileColumn]"
                                item-value="handle"
                                item-text="label"
                        ></v-autocomplete>
                    </div>
                </div>
            </div>
            <div class="form-label right"><span>Felder im gewählten Layer</span></div>
        </div>
        <h3>verbleibende Felder mit Werten füllen</h3>
        <div class="formItem">
            <div class="droparea" @click="addEmptyLayerConfiguration" v-show="canAddLayerColumnsForDefault">
                <i class="icon-plus"></i>
                Feld hinzufügen
            </div>
        </div>
        <transition-group name="slide-fade" tag="div">
            <div class="grid" v-for="(layerColumn, index) in finalLayerConfiguration" :key="index">
                <div class="col-1-2">
                    <div class="formItem button">
                        <label>Feld</label>
                        <v-autocomplete
                                class="sr-select"
                                hide-details
                                v-model="layerColumn.layer"
                                :items="availableLayerColumnsForDefaults(layerColumn.layer)"
                                item-value="handle"
                                item-text="label"
                        ></v-autocomplete>
                        <button class="btn-icon-only icon-trash" @click="removeLayerConfiguration(layerColumn)"></button>
                    </div>
                </div>
                <div class="col-1-2" v-if="getLayerColumnDefinition(layerColumn.layer)">
                    <component
                            :is="getFieldComponent(getLayerColumnDefinition(layerColumn.layer))"
                            :default="getLayerColumnDefinition(layerColumn.layer).default"
                            :instructions="getLayerColumnDefinition(layerColumn.layer).instructions"
                            :label="getLayerColumnDefinition(layerColumn.layer).label"
                            :limit="getLayerColumnDefinition(layerColumn.layer).limit"
                            :items="getLayerColumnDefinition(layerColumn.layer).options"
                            :return-object="getLayerColumnDefinition(layerColumn.layer).returnObject"
                            :suffix="getLayerColumnDefinition(layerColumn.layer).suffix"
                            v-model="layerColumn.default"
                            :loading="loading"
                    ></component>
                </div>
            </div>
        </transition-group>
        <h3>Bestehende Objekte</h3>
        <div>
            <checkbox label="bereits im Layer bestehende Objekte nicht verändern, nur neue Objekte hinzufügen"
                      :inputValue="false"
                      v-model="updateObjects"
                      class="sr-checkbox"></checkbox>
            <checkbox label="bereits im Layer bestehende Objekte verändern und neue Objekte hinzufügen"
                      :inputValue="true"
                      v-model="updateObjects"
                      class="sr-checkbox"></checkbox>
        </div>
    </div>
</template>

<script>
  import _ from 'lodash'

  export default {
    props: {
      value: {
        type: Object,
        required: true
      },
      loading: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        finalFileConfiguration: {},
        finalLayerConfiguration: [],
        objectRights: null,
        flaechenIdField: null
      }
    },
    methods: {
      fileSelectChanged (fileColumn, layerColumn) {
        if (!layerColumn || layerColumn === '') {
          this.$delete(this.finalFileConfiguration, fileColumn)
        } else {
          this.$set(this.finalFileConfiguration, fileColumn, layerColumn)
        }
      },
      addEmptyLayerConfiguration () {
        if (!this.canAddLayerColumnsForDefault) {
          return
        }
        this.finalLayerConfiguration.push({
          layer: null,
          default: null
        })
      },
      getLayerColumnDefinition (layerColumn) {
        return _.find(this.value.layerColumns, (layer) => layer.handle === layerColumn)
      },
      removeLayerConfiguration (layerColumn) {
        if (this.finalLayerConfiguration.indexOf(layerColumn) !== -1) {
          this.finalLayerConfiguration.splice(this.finalLayerConfiguration.indexOf(layerColumn), 1)
        }
      }
    },
    computed: {
      getFieldComponent () {
        return (settings) => {
          switch (settings.type) {
            case 'craft\\fields\\Assets':
              if (settings.allowedKinds && settings.allowedKinds.indexOf('image') > -1 && settings.allowedKinds.length === 1) {
                if (settings.limit === '1') {
                  return 'image-upload'
                }

                return 'gallery-input'
              }

              return 'file-upload'
            case 'craft\\fields\\PlainText':
              if (settings.multiline) {
                return 'text-area'
              }
              return 'text-field'
            case 'craft\\fields\\Categories':
              return 'dropdown'
            case 'modules\\raumkarte\\fields\\Categories':
              return 'dropdown'
            case 'craft\\fields\\Dropdown':
              return 'dropdown'
            case 'craft\\fields\\Number':
              return 'text-field'
            case 'craft\\fields\\Matrix':
              return 'matrix'
            case 'craft\\fields\\Lightswitch':
              return 'lightswitch'
            case 'craft\\fields\\Date':
              return 'date'
          }
        }
      },
      getExampleValues () {
        return (fileColumn) => {
          if (typeof this.value.fileColumnExamples[fileColumn] !== 'undefined') {
            return '("' + this.value.fileColumnExamples[fileColumn].join('", "') + '")'
          }

          return ''
        }
      },
      canAddLayerColumnsForDefault () {
        return (this.availableLayerColumnsForDefaults().length - 1) - _.filter(this.finalLayerConfiguration, (object) => !object.layer).length > 0
      },
      availableLayerColumnsForFiles () {
        return (whiteListFileColumn) => {
          let values = _.values(this.finalFileConfiguration)
          if (values.indexOf(this.finalFileConfiguration[whiteListFileColumn]) > -1) {
            values.splice(values.indexOf(this.finalFileConfiguration[whiteListFileColumn]), 1)
          }
          return _.concat([{ label: '', handle: null }], this.value.layerColumns.filter((layer) => values.indexOf(layer.handle) === -1))
        }
      },
      availableLayerColumnsForDefaults () {
        return (whiteListLayerColumn) => {
          let values = _.map(this.finalLayerConfiguration, (value) => value.layer)
          if (values.indexOf(whiteListLayerColumn) > -1) {
            values.splice(values.indexOf(whiteListLayerColumn), 1)
          }
          return _.concat([{ label: '', handle: null }], this.value.layerColumns.filter((layer) => values.indexOf(layer.handle) === -1))
        }
      },
      updateObjects: {
        get () {
          return this.value.updateObjects
        },
        set (value) {
          this.value.updateObjects = value
        }
      },
      getPossibleObjectRights () {
        return this.$store.getters['auth/getRights']
      }
    },
    watch: {
      finalLayerConfiguration: {
        handler: function (newVal) {
          this.value.layerConfiguration = this.finalLayerConfiguration
        },
        deep: true
      },
      finalFileConfiguration: {
        handler: function (newVal) {
          this.value.fileConfiguration = this.finalFileConfiguration
        },
        deep: true
      },
      objectRights: {
        handler: function (newVal) {
          this.value.objectRights = newVal
        },
        deep: true
      },
      flaechenIdField (newVal) {
        this.value.flaechenIdField = newVal
      }
    }
  }
</script>

<style scoped>

</style>
